import { Box, Container, Typography, Button, Grid } from "@mui/material";
import { Link } from "react-router-dom";
import { useDocumentTitle } from "../../utils/data";
import { keyframes } from "@mui/system";

const floatBall = keyframes`
  0%, 100% { transform: translate(0, 0); }
  25% { transform: translate(20px, -20px); }
  50% { transform: translate(-10px, 20px); }
  75% { transform: translate(10px, -10px); }
`;

const scaleInCenter = keyframes`
  from { opacity: 0; transform: scale(0.85); }
  to { opacity: 1; transform: scale(1); }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-100px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const moveCircle1 = keyframes`
  0%, 100% {
    transform: translate(0, 0);
  }
  25% {
    transform: translate(30px, -40px);
  }
  50% {
    transform: translate(-20px, -30px);
  }
  75% {
    transform: translate(40px, 20px);
  }
`;

const moveCircle2 = keyframes`
  0%, 100% {
    transform: translate(0, 0);
  }
  25% {
    transform: translate(-40px, 20px);
  }
  50% {
    transform: translate(30px, 40px);
  }
  75% {
    transform: translate(-30px, -40px);
  }
`;

const moveCircle3 = keyframes`
  0%, 100% {
    transform: translate(0, 0);
  }
  25% {
    transform: translate(20px, 50px);
  }
  50% {
    transform: translate(-40px, 30px);
  }
  75% {
    transform: translate(40px, -20px);
  }
`;

const serviceOfferings = [
  {
    icon: "✅",
    title: "Manual Testing",
    desc: "Comprehensive exploratory, functional, and user acceptance testing with expert QA engineers across all platforms."
  },
  {
    icon: "🤖",
    title: "Test Automation",
    desc: "Automated testing frameworks for regression, performance, and API testing ensuring faster and more reliable releases."
  },
  {
    icon: "📱",
    title: "Mobile Testing",
    desc: "End-to-end testing for iOS, Android, and cross-platform apps across devices, screen sizes, and OS versions."
  },
  {
    icon: "🔍",
    title: "Quality Assurance",
    desc: "Root cause analysis, defect tracking, and quality metrics to continuously improve application reliability."
  },
  {
    icon: "⚡",
    title: "Performance Testing",
    desc: "Load, stress, and scalability testing to ensure applications perform optimally under demanding conditions."
  },
  {
    icon: "🛡️",
    title: "Security Testing",
    desc: "Vulnerability assessments, penetration testing, and security compliance validation for robust protection."
  }
];

const whyChooseUs = [
  {
    icon: "🎯",
    title: "QA Excellence",
    desc: "Certified QA professionals with expertise across industries and technologies ensuring comprehensive test coverage."
  },
  {
    icon: "⚙️",
    title: "Advanced Tools",
    desc: "Latest test automation tools and frameworks for efficient, scalable, and maintainable testing solutions."
  },
  {
    icon: "📊",
    title: "Data-Driven Approach",
    desc: "Metrics, analytics, and insights that drive quality improvements and risk mitigation throughout development."
  },
  {
    icon: "🚀",
    title: "Early Defect Detection",
    desc: "Continuous quality assurance throughout development lifecycle to catch issues early and reduce costs."
  }
];

const qaExpertise = [
  {
    title: "Functional Testing",
    desc: "Thorough testing of all features and functionalities to ensure they work as specified and expected."
  },
  {
    title: "Test Automation",
    desc: "Build scalable automation frameworks for regression and continuous testing throughout CI/CD pipelines."
  },
  {
    title: "Performance Testing",
    desc: "Identify bottlenecks and optimize applications for speed, responsiveness, and resource efficiency."
  },
  {
    title: "Mobile Testing",
    desc: "Comprehensive testing across iOS, Android, and various devices for seamless user experiences."
  },
  {
    title: "Security Testing",
    desc: "Identify vulnerabilities and ensure applications comply with security standards and best practices."
  }
];

const QualityEngineeringAssurance = () => {
  useDocumentTitle("Briozon - Quality Engineering & Assurance");

  return (
    <Box>
      {/* Hero Banner */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #f8fafc 0%, #f0f4ff 100%)",
          py: { xs: 6, sm: 8, md: 10 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Animated background circles */}
        <Box
          sx={{
            position: "absolute",
            top: "5%",
            right: "10%",
            width: 150,
            height: 150,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(91, 124, 250, 0.15) 0%, rgba(91, 124, 250, 0) 70%)",
            animation: `${moveCircle1} 15s ease-in-out infinite`,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "10%",
            left: "5%",
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(118, 75, 162, 0.12) 0%, rgba(118, 75, 162, 0) 70%)",
            animation: `${moveCircle2} 18s ease-in-out infinite`,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "40%",
            right: "5%",
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(91, 124, 250, 0.1) 0%, rgba(91, 124, 250, 0) 70%)",
            animation: `${moveCircle3} 20s ease-in-out infinite`,
            pointerEvents: "none",
          }}
        />

        {/* Floating decorative elements */}
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            right: "5%",
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(91,124,250,0.1) 0%, rgba(118,75,162,0.1) 100%)",
            animation: `${floatBall} 8s ease-in-out infinite`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "15%",
            left: "3%",
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(118,75,162,0.1) 0%, rgba(91,124,250,0.1) 100%)",
            animation: `${floatBall} 6s ease-in-out infinite 1s`,
          }}
        />

        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, sm: 6, md: 8 }} alignItems="center">
            {/* Left Content */}
            <Grid item xs={12} md={6}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    fontSize: { xs: "12px", sm: "14px" },
                    fontWeight: 700,
                    color: "#5b7cfa",
                    letterSpacing: "2px",
                    mb: 1,
                    display: "block",
                  }}
                >
                  QUALITY ENGINEERING & ASSURANCE
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: "32px", sm: "42px", md: "48px" },
                    fontWeight: 800,
                    color: "#1b1f33",
                    lineHeight: 1.2,
                    mb: 2,
                  }}
                >
                  Excellence in{" "}
                  <Box
                    component="span"
                    sx={{
                      background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    Quality Assurance
                  </Box>
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: "16px", sm: "19px", md: "20px" },
                    color: "#4c5472",
                    lineHeight: 1.8,
                    mb: 3,
                    textAlign: "justify",
                  }}
                >
                  Deliver flawless applications with comprehensive quality engineering and assurance services. Our expert QA team ensures your software meets the highest standards for functionality, performance, security, and user experience.
                </Typography>
                <Button
                  component={Link}
                  to="/contact"
                  variant="contained"
                  sx={{
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    color: "#fff",
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    borderRadius: "12px",
                    fontSize: { xs: "16px", sm: "19px", md: "20px" },
                    textTransform: "none",
                    boxShadow: "0 8px 24px rgba(91, 124, 250, 0.3)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 32px rgba(91, 124, 250, 0.4)",
                    },
                  }}
                >
                  Get Started
                </Button>
              </Box>
            </Grid>

            {/* Right - Creative Visual */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  height: { xs: 320, sm: 400, md: 450 },
                  animation: `${scaleInCenter} 0.8s ease-out 0.2s both`,
                }}
              >
                {/* Main gradient circle */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 220, sm: 280, md: 320 },
                    height: { xs: 220, sm: 280, md: 320 },
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, rgba(91,124,250,0.15) 0%, rgba(118,75,162,0.15) 100%)",
                    filter: "blur(40px)",
                  }}
                />

                {/* Central icon */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 100, sm: 120 },
                    height: { xs: 100, sm: 120 },
                    borderRadius: "24px",
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: { xs: "48px", sm: "56px" },
                    boxShadow: "0 20px 40px rgba(91, 124, 250, 0.4)",
                    animation: `${floatBall} 6s ease-in-out infinite`,
                    zIndex: 2,
                  }}
                >
                  ✅
                </Box>

                {/* Floating feature cards */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "8%",
                    left: "10%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 7s ease-in-out infinite 0.5s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>🤖</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    Automation
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    top: "15%",
                    right: "5%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 8s ease-in-out infinite 1s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>📱</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    Mobile Testing
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: "20%",
                    left: "5%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 9s ease-in-out infinite 1.5s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>⚡</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    Performance
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: "10%",
                    right: "10%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 6s ease-in-out infinite 2s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>🛡️</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    Security
                  </Typography>
                </Box>

                {/* Decorative rings */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 200, sm: 260 },
                    height: { xs: 200, sm: 260 },
                    borderRadius: "50%",
                    border: "2px dashed rgba(91, 124, 250, 0.2)",
                    animation: `${floatBall} 10s ease-in-out infinite`,
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 280, sm: 360 },
                    height: { xs: 280, sm: 360 },
                    borderRadius: "50%",
                    border: "1px dashed rgba(118, 75, 162, 0.15)",
                    animation: `${floatBall} 12s ease-in-out infinite reverse`,
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Section 2: Comprehensive QA Testing & Assurance */}
      <Box sx={{ py: { xs: 8, md: 10 }, backgroundColor: "#fff" }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="flex-start">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  width: 60,
                  height: 4,
                  background: "linear-gradient(90deg, #5b7cfa 0%, #764ba2 100%)",
                  borderRadius: "2px",
                  mb: 3,
                  animation: `${slideInLeft} 0.8s ease-out`,
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "28px", sm: "36px", md: "42px" },
                  color: "#1b1f33",
                  mb: 3,
                  animation: `${slideInLeft} 0.8s ease-out 0.1s both`,
                }}
              >
                Comprehensive QA{" "}
                <Box
                  component="span"
                  sx={{
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  Testing & Assurance
                </Box>
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#4c5472",
                  lineHeight: 1.8,
                  fontSize: { xs: "16px", sm: "19px", md: "20px" },
                  mb: 3,
                  textAlign: "justify",
                  animation: `${fadeInUp} 0.8s ease-out 0.2s both`,
                }}
              >
                Our quality engineering team provides end-to-end testing and assurance services across web, mobile, and desktop applications. We combine manual expertise with advanced test automation to identify defects early, reduce risks, and ensure your software meets the highest quality standards.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#4c5472",
                  lineHeight: 1.8,
                  fontSize: { xs: "16px", sm: "19px", md: "20px" },
                  textAlign: "justify",
                  animation: `${fadeInUp} 0.8s ease-out 0.3s both`,
                }}
              >
                From functional and regression testing to performance and security validation, we deliver comprehensive coverage across all testing disciplines. Our data-driven approach, combined with industry best practices, ensures continuous quality improvements throughout your development lifecycle.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: "20px",
                  background: "linear-gradient(135deg, #f0f4ff 0%, #f7f0ff 100%)",
                  animation: `${fadeInUp} 0.8s ease-out 0.2s both`,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: "#1b1f33", mb: 3, fontSize: { xs: "18px", sm: "20px" } }}
                >
                  Testing Disciplines
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  {["Manual Testing", "Test Automation", "Mobile Testing", "Performance", "Security Testing", "QA Tools"].map((tech) => (
                    <Box
                      key={tech}
                      sx={{
                        px: 3,
                        py: 1.2,
                        borderRadius: "30px",
                        background: "#fff",
                        color: "#5b7cfa",
                        fontWeight: 600,
                        fontSize: { xs: "14px", sm: "15px" },
                        border: "1.5px solid rgba(91, 124, 250, 0.3)",
                        boxShadow: "0 2px 8px rgba(91, 124, 250, 0.08)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                          color: "#fff",
                          borderColor: "transparent",
                          transform: "translateY(-2px)",
                          boxShadow: "0 6px 16px rgba(91, 124, 250, 0.25)",
                        },
                      }}
                    >
                      {tech}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Section 3: Expertise - Alternating Layout */}
      <Box sx={{ py: { xs: 8, md: 10 }, backgroundColor: "#fafbff" }}>
        <Container maxWidth="lg">
          {/* Row 1: QA Expertise */}
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center" sx={{ mb: { xs: 8, md: 10 } }}>
            <Grid item xs={12} md={6}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "28px", sm: "36px", md: "42px" },
                  color: "#1b1f33",
                  mb: 3,
                }}
              >
                Our QA & Testing{" "}
                <Box
                  component="span"
                  sx={{
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Expertise
                </Box>
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#4c5472",
                  lineHeight: 1.8,
                  fontSize: { xs: "16px", sm: "19px", md: "20px" },
                  textAlign: "justify",
                }}
              >
                Briozon specializes in quality engineering and comprehensive assurance services that ensure your software delivers excellence. We combine proven testing methodologies with cutting-edge tools to deliver reliable, performant, and secure applications.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: "20px",
                  background: "linear-gradient(135deg, rgba(91,124,250,0.06) 0%, rgba(118,75,162,0.06) 100%)",
                  border: "1px solid rgba(91,124,250,0.12)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                }}
              >
                {[
                  { icon: "🤖", label: "Automated Testing Frameworks" },
                  { icon: "📊", label: "Test Coverage & Metrics" },
                  { icon: "🔗", label: "CI/CD Pipeline Integration" },
                ].map((item) => (
                  <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "12px",
                        background: "linear-gradient(135deg, rgba(91,124,250,0.12), rgba(118,75,162,0.12))",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.3rem",
                        flexShrink: 0,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: { xs: "16px", sm: "18px" } }}>
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>

          {/* Row 2: Ensuring Quality at Every Step */}
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
            <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 1 } }}>
              <Box
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: "20px",
                  background: "linear-gradient(135deg, rgba(118,75,162,0.06) 0%, rgba(91,124,250,0.06) 100%)",
                  border: "1px solid rgba(118,75,162,0.12)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                }}
              >
                {[
                  { icon: "🎯", label: "Early Defect Detection" },
                  { icon: "🛡️", label: "Security & Compliance Checks" },
                  { icon: "📈", label: "Continuous Quality Improvement" },
                ].map((item) => (
                  <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "12px",
                        background: "linear-gradient(135deg, rgba(118,75,162,0.12), rgba(91,124,250,0.12))",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.3rem",
                        flexShrink: 0,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: { xs: "16px", sm: "18px" } }}>
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "28px", sm: "36px", md: "42px" },
                  color: "#1b1f33",
                  mb: 3,
                }}
              >
                Ensuring Quality{" "}
                <Box
                  component="span"
                  sx={{
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  at Every Step
                </Box>
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#4c5472",
                  lineHeight: 1.8,
                  fontSize: { xs: "16px", sm: "19px", md: "20px" },
                  mb: 3,
                  textAlign: "justify",
                }}
              >
                Quality is not a phase—it's built throughout the entire development lifecycle. Our comprehensive testing strategy catches issues early, reduces defects, and delivers software your users can trust.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#4c5472",
                  lineHeight: 1.8,
                  fontSize: { xs: "16px", sm: "19px", md: "20px" },
                  textAlign: "justify",
                }}
              >
                From day one through post-launch monitoring, we apply rigorous testing disciplines across functional, performance, security, and user experience dimensions. Our goal is to help you deliver software that exceeds expectations and builds customer confidence in your brand.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Section 4: QA Expertise Cards */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: "linear-gradient(135deg, #f0f4ff 0%, #f7f0ff 100%)",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 6 } }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                color: "#1b1f33",
                mb: 2,
              }}
            >
              Our Quality Assurance{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Services
              </Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#4c5472",
                lineHeight: 1.8,
                maxWidth: 700,
                mx: "auto",
                fontSize: { xs: "16px", sm: "19px", md: "20px" },
              }}
            >
              Complete testing coverage to ensure your applications are reliable, performant, and secure.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
              gap: { xs: 3, sm: 4 },
            }}
          >
            {qaExpertise.map((item, index) => (
              <Box
                key={index}
                sx={{
                  p: { xs: 3, sm: 4 },
                  background: "#fff",
                  borderRadius: "16px",
                  border: "1px solid #e8eaf0",
                  borderLeft: "4px solid transparent",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                  transition: "all 0.3s ease",
                  animation: `${fadeInUp} 0.6s ease-out ${index * 0.08}s both`,
                  "&:hover": {
                    transform: "translateY(-6px)",
                    boxShadow: "0 16px 40px rgba(91, 124, 250, 0.14)",
                    borderLeft: "4px solid #5b7cfa",
                    borderColor: "#e8eaf0",
                    "& .qa-title": { color: "#5b7cfa" },
                  },
                }}
              >
                <Typography
                  className="qa-title"
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "#1b1f33",
                    mb: 1.5,
                    fontSize: { xs: "20px", sm: "22px" },
                    transition: "color 0.3s ease",
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#4c5472",
                    lineHeight: 1.7,
                    fontSize: { xs: "16px", sm: "17px" },
                  }}
                >
                  {item.desc}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Section 5: Why Choose Us */}
      <Box sx={{ py: { xs: 8, md: 10 }, backgroundColor: "#fff" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 6 } }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                color: "#1b1f33",
                mb: 1,
              }}
            >
              Why Choose Briozon for{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                QA Services
              </Box>
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {whyChooseUs.map((item, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box
                  sx={{
                    p: { xs: 3, sm: 4 },
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 3,
                    borderRadius: "16px",
                    backgroundColor: "#fafbff",
                    border: "1px solid #e8eaf0",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                    transition: "all 0.3s ease",
                    animation: `${fadeInUp} 0.6s ease-out ${index * 0.08}s both`,
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 32px rgba(91, 124, 250, 0.12)",
                      borderColor: "rgba(91, 124, 250, 0.3)",
                      backgroundColor: "#fff",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: "52px", sm: "58px" },
                      height: { xs: "52px", sm: "58px" },
                      borderRadius: "14px",
                      background: "linear-gradient(135deg, rgba(91,124,250,0.12), rgba(118,75,162,0.12))",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: { xs: "26px", sm: "30px" },
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "#1b1f33",
                        mb: 1,
                        fontSize: { xs: "20px", sm: "22px" },
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#4c5472",
                        lineHeight: 1.7,
                        fontSize: { xs: "16px", sm: "17px" },
                      }}
                    >
                      {item.desc}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Section 6: Service Offerings */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: "linear-gradient(135deg, #f0f4ff 0%, #f7f0ff 100%)",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 6 } }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                color: "#1b1f33",
                mb: 2,
              }}
            >
              Our QA & Testing{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Solutions
              </Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#4c5472",
                lineHeight: 1.8,
                maxWidth: 700,
                mx: "auto",
                fontSize: { xs: "16px", sm: "19px", md: "20px" },
              }}
            >
              End-to-end quality assurance from test planning through post-launch quality monitoring.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
              gap: { xs: 3, sm: 4 },
            }}
          >
            {serviceOfferings.map((item, index) => (
              <Box
                key={index}
                sx={{
                  p: { xs: 3, sm: 4 },
                  background: "#fff",
                  borderRadius: "16px",
                  border: "1px solid #e8eaf0",
                  borderLeft: "4px solid transparent",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                  transition: "all 0.3s ease",
                  animation: `${fadeInUp} 0.6s ease-out ${index * 0.08}s both`,
                  "&:hover": {
                    transform: "translateY(-6px)",
                    boxShadow: "0 16px 40px rgba(91, 124, 250, 0.14)",
                    borderLeft: "4px solid #5b7cfa",
                    borderColor: "#e8eaf0",
                    "& .offering-icon": {
                      transform: "scale(1.12)",
                      background: "linear-gradient(135deg, rgba(91,124,250,0.25), rgba(118,75,162,0.25))",
                    },
                    "& .offering-title": { color: "#5b7cfa" },
                  },
                }}
              >
                <Box
                  className="offering-icon"
                  sx={{
                    width: { xs: "52px", sm: "58px" },
                    height: { xs: "52px", sm: "58px" },
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 2,
                    borderRadius: "14px",
                    background: "linear-gradient(135deg, rgba(91,124,250,0.12), rgba(118,75,162,0.12))",
                    fontSize: { xs: "26px", sm: "30px" },
                    transition: "all 0.3s ease",
                  }}
                >
                  {item.icon}
                </Box>
                <Typography
                  className="offering-title"
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "#1b1f33",
                    mb: 1.5,
                    fontSize: { xs: "20px", sm: "22px" },
                    transition: "color 0.3s ease",
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#4c5472",
                    lineHeight: 1.7,
                    fontSize: { xs: "16px", sm: "17px" },
                  }}
                >
                  {item.desc}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Contact Section */}
      <Box
        sx={{
          py: { xs: 8, sm: 10, md: 12 },
          background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative circles */}
        <Box
          sx={{
            position: "absolute",
            top: "-50px",
            right: "-50px",
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.1)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "-30px",
            left: "-30px",
            width: 150,
            height: 150,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
          }}
        />

        <Container maxWidth="md">
          <Box sx={{ textAlign: "center", position: "relative", zIndex: 1 }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                fontWeight: 800,
                color: "#fff",
                mb: 2,
              }}
            >
              Ready to Ensure Quality Excellence?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: "16px", sm: "19px", md: "20px" },
                color: "rgba(255,255,255,0.9)",
                mb: 4,
                maxWidth: "700px",
                mx: "auto",
                lineHeight: 1.8,
              }}
            >
              Let's discuss how comprehensive QA services can improve your software quality and reduce defects. Schedule a consultation with our testing experts to develop a customized QA strategy.
            </Typography>
            <Button
              component="a"
              href="/contact"
              variant="contained"
              sx={{
                backgroundColor: "#fff",
                color: "#5b7cfa",
                fontWeight: 700,
                px: 5,
                py: 1.5,
                borderRadius: "12px",
                fontSize: { xs: "16px", sm: "19px", md: "20px" },
                textTransform: "none",
                transition: "all 0.3s ease",
                boxShadow: "0 8px 24px rgba(91, 124, 250, 0.08)",
                "&:hover": {
                  backgroundColor: "#f0f0f0",
                  transform: "translateY(-4px)",
                  boxShadow: "0 12px 24px rgba(0,0,0,0.12)",
                },
              }}
            >
              Let's Connect
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default QualityEngineeringAssurance;
